// Има ли сметката фискална фактура — тогава сторното ѝ („Сторнирай всичко",
// „Възстанови") е КРЕДИТНО ИЗВЕСТИЕ от фискалното устройство, само за цялата
// сметка (BE отказва частичното).
//
// `/bills/history` (BE ≥ 0.0.382) носи номера на живата фактура и дали е
// фискална (`invoice_number`, `invoice_is_fiscal`) — решава се от реда, без
// заявка: и за човек без правото да вижда фактури (GET /invoices е 403 за него
// и предупреждението изчезваше). По-старо BE без полето — както преди: теглят се
// фактурите около деня на закриването (един ден преди и след — датата на
// издаване е денят на бона, а `closed_at` е UTC) и се търси активната фискална с
// тази сметка, която е и живата фактура на реда. Само за показване: BE решава
// сам и без това.

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { listInvoices, type Invoice } from "../api/invoices";
import type { BillHistory } from "../api/reportsBillsHistory";
import { isCreditNote, isFiscalInvoiceDocument } from "../lib/fiscalInvoice";

/** Фискалната фактура, към която ще излезе кредитното известие. */
export interface BillFiscalInvoice {
  invoice_number: string;
}

/** Активната фискална фактура на сметката сред фактурите на периода.
 *
 * `bill_ids` носи и неактивните връзки (фактура, кредитирана при „Възстанови")
 * — затова, когато редът знае номера на живата фактура (`liveNumber`), тя
 * трябва да е точно тази: иначе веригата фискална → КИ → софтуерна обещава
 * кредитно известие към вече кредитираната. */
export const billFiscalInvoiceOf = (
  invoices: readonly Invoice[],
  billId: number,
  liveNumber?: string | null,
): Invoice | null =>
  invoices.find(
    (invoice) =>
      !invoice.cancelled_at &&
      isFiscalInvoiceDocument(invoice) &&
      !isCreditNote(invoice) &&
      (invoice.bill_ids ?? []).includes(billId) &&
      (liveNumber === undefined || invoice.invoice_number === liveNumber),
  ) ?? null;

export const useBillFiscalInvoice = (bill: BillHistory | null): BillFiscalInvoice | null => {
  const knowsKind = typeof bill?.invoice_is_fiscal === "boolean";
  const closedAt = bill?.closed_at ? dayjs(bill.closed_at) : null;
  const filters = closedAt
    ? {
        date_from: closedAt.subtract(1, "day").format("YYYY-MM-DD"),
        date_to: closedAt.add(1, "day").format("YYYY-MM-DD"),
      }
    : {};
  // Същият ключ като справка „Фактури" — издаване/сторно инвалидира ["invoices"].
  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices", "list", filters],
    queryFn: () => listInvoices(filters),
    staleTime: 1000 * 60,
    enabled:
      !knowsKind && !!bill && bill.status === "closed" && bill.is_invoiced === true && closedAt !== null,
  });
  if (!bill) return null;
  if (knowsKind) {
    return bill.invoice_is_fiscal && bill.invoice_number ? { invoice_number: bill.invoice_number } : null;
  }
  return billFiscalInvoiceOf(invoices, bill.id, bill.invoice_number);
};
